/**
 * DoraMetricsStore - MobX store for calculating DORA metrics
 *
 * This store computes the four key DORA metrics:
 * 1. Deployment Frequency - How often we release to production
 * 2. Lead Time for Changes - Time from commit to production
 * 3. Change Failure Rate - Percentage of deployments causing failures
 * 4. Time to Restore Service - Time to recover from failures
 *
 * Data sources:
 * - Releases data for deployment frequency
 * - Commits and PRs for lead time calculations
 * - Commit types (bugfix, revert) for failure estimation
 */

import { makeAutoObservable, runInAction } from "mobx"
import { fetchJson } from "../lib/fetch"
import { logger } from "../lib/logger"

// Translation keys for missing data messages
export type DoraMissingDataKey =
  | "doraMissingReleases"
  | "doraMissingPullRequests"
  | "doraMissingCommits"
  | "doraMissingDeployments"
  | "doraMissingWorkflowRuns"
  | "doraMissingIncidents"

// SQLite exports from backend
export interface ReleaseData {
  id: number
  repo_id: number
  tag_name: string
  name?: string
  author_id: number
  created_at: number // Unix timestamp
  published_at?: number // Unix timestamp
  draft: boolean
  prerelease: boolean
}

export interface PullRequestData {
  id: number
  repo_id: number
  number: number
  title: string
  creator_id: number
  created_at: number
  merged_at?: number
  closed_at?: number
  state: string
  draft: boolean
  additions: number
  deletions: number
  changed_files: number
  comments: number
}

export interface CommitData {
  hash: string
  repo_id: number
  author_id: number
  timestamp: number
  score: number
  class: string
  commit_type?: string
  message: string
  files_changed: number
  insertions: number
  deletions: number
  functions_changed: number
  cyclomatic_delta: number
  max_nesting_delta: number
}

export interface RepositoryData {
  id: number
  owner: string
  repo: string
  full_name: string
}

// Phase 2: Workflow and Deployment data
export interface WorkflowRunData {
  id: number
  repo_id: number
  workflow_id: number
  workflow_name: string
  commit_sha: string
  branch: string
  event: string
  status: string
  conclusion?: string
  run_number: number
  created_at: number
  updated_at: number
  run_started_at?: number
  run_completed_at?: number
  run_duration_seconds?: number
  actor_id?: number
}

export interface DeploymentData {
  id: number
  repo_id: number
  commit_sha: string
  ref: string
  environment: string
  description?: string
  creator_id?: number
  created_at: number
  updated_at: number
}

export interface DeploymentStatusData {
  id: number
  deployment_id: number
  state: string
  description?: string
  environment?: string
  created_at: number
  updated_at: number
}

// Phase 3: Incident tracking data
export interface IssueData {
  id: number
  repo_id: number
  number: number
  title: string
  creator_id: number
  created_at: number
  closed_at?: number
  closed_by_id?: number
  state: string
  comments: number
  comment_authors?: number
  first_comment_at?: number
  last_comment_at?: number
  // Phase 3 fields
  is_incident?: boolean
  incident_severity?: string
  incident_detected_at?: number
  incident_resolved_at?: number
  labels?: string
}

// Phase 4: Pre-calculated DORA metrics from backend
export interface DoraMetricData {
  id?: number
  repo_id: number
  author_id?: number // NULL for repo-level metrics
  period_type: "day" | "week" | "month"
  period_start: number // Unix timestamp
  period_end: number // Unix timestamp

  // Deployment Frequency
  deployment_count: number
  deployments_per_day: number

  // Lead Time for Changes
  lead_time_avg_hours?: number
  lead_time_median_hours?: number
  lead_time_p95_hours?: number
  lead_time_samples: number

  // Change Failure Rate (repo-level only)
  total_deployments?: number
  failed_deployments?: number
  failure_rate_percent?: number

  // Time to Restore (repo-level only)
  incident_count?: number
  mttr_avg_hours?: number
  mttr_median_hours?: number

  calculated_at: number // Unix timestamp
}

// Computed metrics
export interface DeploymentFrequency {
  period: string // e.g., "2024-11"
  count: number
  deploymentsPerDay: number
}

export interface LeadTimeMetric {
  period: string
  averageHours: number
  medianHours: number
  p95Hours: number
}

export interface ChangeFailureMetric {
  period: string
  totalDeployments: number
  failedDeployments: number
  failureRate: number // percentage
}

export interface TimeToRestoreMetric {
  period: string
  averageHours: number
  medianHours: number
  incidents: number
}

export interface DoraPerformanceLevel {
  deploymentFrequency: "elite" | "high" | "medium" | "low"
  leadTime: "elite" | "high" | "medium" | "low"
  changeFailureRate: "elite" | "high" | "medium" | "low"
  timeToRestore: "elite" | "high" | "medium" | "low"
  overall: "elite" | "high" | "medium" | "low"
}

export class DoraMetricsStore {
  // Loading state
  isLoading = false
  loadError: string | null = null

  // Pre-calculated DORA metrics (Phase 4)
  doraMetrics: DoraMetricData[] = []

  // Legacy data (kept for backward compatibility and fallback)
  releases: ReleaseData[] = []
  pullRequests: PullRequestData[] = []
  commits: CommitData[] = []
  repositories: RepositoryData[] = []

  // Phase 2 & 3 data (deprecated - used only as fallback)
  workflowRuns: WorkflowRunData[] = []
  deployments: DeploymentData[] = []
  deploymentStatuses: DeploymentStatusData[] = []
  issues: IssueData[] = []

  // Time range filter (default: last 90 days)
  startDate: Date = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
  endDate: Date = new Date()

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Load data from backend exports
   */
  async initialize(): Promise<void> {
    if (this.isLoading) {
      logger.debug("DoraMetricsStore already initializing, skipping", {
        component: "DoraMetricsStore",
      })
      return
    }

    try {
      runInAction(() => {
        this.isLoading = true
        this.loadError = null
      })

      logger.info("Initializing DoraMetricsStore...", {
        component: "DoraMetricsStore",
      })

      // Try to load pre-calculated DORA metrics first (Phase 4)
      const doraMetrics = await fetchJson<DoraMetricData[]>("/data/dora_metrics.json").catch(
        (error) => {
          logger.warn("Failed to load dora_metrics.json - will use fallback calculation", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as DoraMetricData[]
        },
      )

      // Load legacy data for fallback and context
      const [
        releases,
        pullRequests,
        commits,
        repositories,
        workflowRuns,
        deployments,
        deploymentStatuses,
        issues,
      ] = await Promise.all([
        fetchJson<ReleaseData[]>("/data/releases.json").catch((error) => {
          logger.warn("Failed to load releases.json", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as ReleaseData[]
        }),
        fetchJson<PullRequestData[]>("/data/pull_requests.json").catch((error) => {
          logger.warn("Failed to load pull_requests.json", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as PullRequestData[]
        }),
        fetchJson<CommitData[]>("/data/commits.json").catch((error) => {
          logger.warn("Failed to load commits.json", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as CommitData[]
        }),
        fetchJson<RepositoryData[]>("/data/repositories.json").catch((error) => {
          logger.warn("Failed to load repositories.json", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as RepositoryData[]
        }),
        // Phase 2 data
        fetchJson<WorkflowRunData[]>("/data/workflow_runs.json").catch((error) => {
          logger.warn("Failed to load workflow_runs.json - Phase 2 data not available", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as WorkflowRunData[]
        }),
        fetchJson<DeploymentData[]>("/data/deployments.json").catch((error) => {
          logger.warn("Failed to load deployments.json - Phase 2 data not available", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as DeploymentData[]
        }),
        fetchJson<DeploymentStatusData[]>("/data/deployment_statuses.json").catch((error) => {
          logger.warn("Failed to load deployment_statuses.json - Phase 2 data not available", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as DeploymentStatusData[]
        }),
        // Phase 3 data
        fetchJson<IssueData[]>("/data/issues.json").catch((error) => {
          logger.warn("Failed to load issues.json - Phase 3 data not available", {
            component: "DoraMetricsStore",
            error,
          })
          return [] as IssueData[]
        }),
      ])

      runInAction(() => {
        this.doraMetrics = doraMetrics
        this.releases = releases
        this.pullRequests = pullRequests
        this.commits = commits
        this.repositories = repositories
        this.workflowRuns = workflowRuns
        this.deployments = deployments
        this.deploymentStatuses = deploymentStatuses
        this.issues = issues
      })

      logger.info("DoraMetricsStore initialized successfully", {
        component: "DoraMetricsStore",
        doraMetricsCount: doraMetrics.length,
        releasesCount: releases.length,
        pullRequestsCount: pullRequests.length,
        commitsCount: commits.length,
        repositoriesCount: repositories.length,
        workflowRunsCount: workflowRuns.length,
        deploymentsCount: deployments.length,
        deploymentStatusesCount: deploymentStatuses.length,
        issuesCount: issues.length,
        incidentsCount: issues.filter((i) => i.is_incident).length,
        usingPreCalculatedMetrics: doraMetrics.length > 0,
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to initialize DoraMetricsStore"

      runInAction(() => {
        this.loadError = errorMessage
      })

      logger.error("Failed to initialize DoraMetricsStore", error, {
        component: "DoraMetricsStore",
      })

      throw error
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Set time range for metrics calculation
   */
  setTimeRange(startDate: Date, endDate: Date): void {
    this.startDate = startDate
    this.endDate = endDate
  }

  /**
   * Get filtered pre-calculated DORA metrics within time range
   */
  get filteredDoraMetrics(): DoraMetricData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.doraMetrics.filter(
      (metric) =>
        metric.period_start >= startTimestamp &&
        metric.period_end <= endTimestamp &&
        metric.author_id === undefined, // Repo-level metrics only
    )
  }

  /**
   * Get filtered per-author DORA metrics within time range
   */
  getFilteredDoraMetricsByAuthor(authorId?: number): DoraMetricData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.doraMetrics.filter(
      (metric) =>
        metric.period_start >= startTimestamp &&
        metric.period_end <= endTimestamp &&
        (authorId === undefined || metric.author_id === authorId),
    )
  }

  /**
   * Check if pre-calculated metrics are available and recent
   */
  get hasPreCalculatedMetrics(): boolean {
    return this.doraMetrics.length > 0
  }

  /**
   * Get the timestamp when metrics were last calculated
   */
  get metricsCalculatedAt(): Date | null {
    if (this.doraMetrics.length === 0) return null

    // Get the most recent calculated_at timestamp
    const latestTimestamp = Math.max(...this.doraMetrics.map((m) => m.calculated_at))
    return new Date(latestTimestamp * 1000)
  }

  /**
   * Get filtered releases within time range
   */
  get filteredReleases(): ReleaseData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.releases.filter((release) => {
      const timestamp = release.published_at || release.created_at
      return (
        timestamp >= startTimestamp &&
        timestamp <= endTimestamp &&
        !release.draft &&
        !release.prerelease
      )
    })
  }

  /**
   * Get filtered commits within time range
   */
  get filteredCommits(): CommitData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.commits.filter(
      (commit) => commit.timestamp >= startTimestamp && commit.timestamp <= endTimestamp,
    )
  }

  /**
   * Get filtered PRs within time range
   */
  get filteredPullRequests(): PullRequestData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.pullRequests.filter((pr) => {
      const timestamp = pr.merged_at || pr.created_at
      return timestamp >= startTimestamp && timestamp <= endTimestamp && pr.state === "closed"
    })
  }

  /**
   * Get filtered deployments within time range (Phase 2)
   */
  get filteredDeployments(): DeploymentData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.deployments.filter(
      (deployment) =>
        deployment.created_at >= startTimestamp &&
        deployment.created_at <= endTimestamp &&
        deployment.environment === "production", // Only count production deployments
    )
  }

  /**
   * Get filtered workflow runs within time range (Phase 2)
   */
  get filteredWorkflowRuns(): WorkflowRunData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.workflowRuns.filter(
      (run) =>
        run.created_at >= startTimestamp &&
        run.created_at <= endTimestamp &&
        run.status === "completed",
    )
  }

  /**
   * Get filtered incidents within time range (Phase 3)
   */
  get filteredIncidents(): IssueData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.issues.filter(
      (issue) =>
        issue.is_incident &&
        issue.incident_detected_at &&
        issue.incident_detected_at >= startTimestamp &&
        issue.incident_detected_at <= endTimestamp,
    )
  }

  /**
   * Get deployment success/failure status from deployment_statuses
   */
  private getDeploymentStatus(deploymentId: number): "success" | "failure" | "pending" | "unknown" {
    const statuses = this.deploymentStatuses
      .filter((s) => s.deployment_id === deploymentId)
      .sort((a, b) => b.created_at - a.created_at)

    if (statuses.length === 0) return "unknown"

    const latestStatus = statuses[0]
    if (latestStatus.state === "success") return "success"
    if (latestStatus.state === "failure" || latestStatus.state === "error") return "failure"
    return "pending"
  }

  /**
   * Calculate deployment frequency by month
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 2/releases
   */
  get deploymentFrequency(): DeploymentFrequency[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      const monthlyMetrics = metrics.filter((m) => m.period_type === "month")

      if (monthlyMetrics.length > 0) {
        return monthlyMetrics
          .map((metric) => {
            const date = new Date(metric.period_start * 1000)
            const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

            return {
              period,
              count: metric.deployment_count,
              deploymentsPerDay: metric.deployments_per_day,
            }
          })
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const frequencyMap = new Map<string, number>()

    // Prefer deployment data (Phase 2) over releases
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0

    if (useDeployments) {
      // Phase 2: Use actual deployment events
      for (const deployment of this.filteredDeployments) {
        const timestamp = deployment.created_at * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        frequencyMap.set(period, (frequencyMap.get(period) || 0) + 1)
      }
    } else {
      // Fallback: Use releases as proxy for deployments
      for (const release of this.filteredReleases) {
        const timestamp = (release.published_at || release.created_at) * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        frequencyMap.set(period, (frequencyMap.get(period) || 0) + 1)
      }
    }

    const result: DeploymentFrequency[] = []
    for (const [period, count] of frequencyMap.entries()) {
      const [year, month] = period.split("-").map(Number)
      const daysInMonth = new Date(year, month, 0).getDate()

      result.push({
        period,
        count,
        deploymentsPerDay: count / daysInMonth,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average deployment frequency metrics
   * Phase 4: Uses pre-calculated metrics if available
   */
  get averageDeploymentFrequency(): {
    perDay: number
    perWeek: number
    perMonth: number
  } {
    const totalDays = Math.ceil(
      (this.endDate.getTime() - this.startDate.getTime()) / (1000 * 60 * 60 * 24),
    )

    // Prefer deployment data (Phase 2) over releases
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0
    const totalDeployments = useDeployments
      ? this.filteredDeployments.length
      : this.filteredReleases.length

    return {
      perDay: totalDeployments / totalDays,
      perWeek: (totalDeployments / totalDays) * 7,
      perMonth: (totalDeployments / totalDays) * 30,
    }
  }

  /**
   * Calculate lead time for changes
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 2/PR timeline
   */
  get leadTimeForChanges(): LeadTimeMetric[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      const monthlyMetrics = metrics.filter(
        (m) => m.period_type === "month" && m.lead_time_samples > 0,
      )

      if (monthlyMetrics.length > 0) {
        return monthlyMetrics
          .map((metric) => {
            const date = new Date(metric.period_start * 1000)
            const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

            return {
              period,
              averageHours: metric.lead_time_avg_hours || 0,
              medianHours: metric.lead_time_median_hours || 0,
              p95Hours: metric.lead_time_p95_hours || 0,
            }
          })
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const leadTimeMap = new Map<string, number[]>()

    // Prefer deployment-based lead time (Phase 2)
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0

    if (useDeployments) {
      // Phase 2: Calculate lead time from commit to deployment
      for (const deployment of this.filteredDeployments) {
        // Find the commit for this deployment
        const commit = this.commits.find(
          (c) => c.hash === deployment.commit_sha && c.repo_id === deployment.repo_id,
        )

        if (commit) {
          // Lead time = deployment time - commit time
          const leadTimeHours = (deployment.created_at - commit.timestamp) / 3600

          const date = new Date(deployment.created_at * 1000)
          const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

          if (!leadTimeMap.has(period)) {
            leadTimeMap.set(period, [])
          }
          // Only count positive lead times (deployment after commit)
          if (leadTimeHours >= 0) {
            leadTimeMap.get(period)?.push(leadTimeHours)
          }
        }
      }
    } else {
      // Fallback: Use PR creation to merge time
      for (const pr of this.filteredPullRequests) {
        if (!pr.merged_at) continue

        const leadTimeHours = (pr.merged_at - pr.created_at) / 3600
        const date = new Date(pr.merged_at * 1000)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!leadTimeMap.has(period)) {
          leadTimeMap.set(period, [])
        }
        leadTimeMap.get(period)?.push(leadTimeHours)
      }
    }

    const result: LeadTimeMetric[] = []
    for (const [period, times] of leadTimeMap.entries()) {
      if (times.length === 0) continue

      times.sort((a, b) => a - b)
      const avg = times.reduce((sum, t) => sum + t, 0) / times.length
      const median = times[Math.floor(times.length / 2)]
      const p95 = times[Math.floor(times.length * 0.95)]

      result.push({
        period,
        averageHours: avg,
        medianHours: median,
        p95Hours: p95,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average lead time
   * Phase 2: Uses commit → deployment when available
   */
  get averageLeadTime(): {
    hours: number
    days: number
  } {
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0

    if (useDeployments) {
      // Phase 2: Calculate from commit to deployment
      let totalLeadTimeHours = 0
      let count = 0

      for (const deployment of this.filteredDeployments) {
        const commit = this.commits.find(
          (c) => c.hash === deployment.commit_sha && c.repo_id === deployment.repo_id,
        )

        if (commit) {
          const leadTimeHours = (deployment.created_at - commit.timestamp) / 3600
          if (leadTimeHours >= 0) {
            totalLeadTimeHours += leadTimeHours
            count++
          }
        }
      }

      if (count === 0) {
        return { hours: 0, days: 0 }
      }

      const avgHours = totalLeadTimeHours / count
      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    } else {
      // Fallback: Use PR timeline
      const mergedPRs = this.filteredPullRequests.filter((pr) => pr.merged_at)
      if (mergedPRs.length === 0) {
        return { hours: 0, days: 0 }
      }

      const totalLeadTimeHours = mergedPRs.reduce((sum, pr) => {
        if (!pr.merged_at) return sum
        const leadTimeHours = (pr.merged_at - pr.created_at) / 3600
        return sum + leadTimeHours
      }, 0)

      const avgHours = totalLeadTimeHours / mergedPRs.length

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    }
  }

  /**
   * Calculate change failure rate
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 2 & 3
   */
  get changeFailureRate(): ChangeFailureMetric[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      const monthlyMetrics = metrics.filter(
        (m) =>
          m.period_type === "month" && m.total_deployments !== undefined && m.total_deployments > 0,
      )

      if (monthlyMetrics.length > 0) {
        return monthlyMetrics
          .map((metric) => {
            const date = new Date(metric.period_start * 1000)
            const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

            return {
              period,
              totalDeployments: metric.total_deployments || 0,
              failedDeployments: metric.failed_deployments || 0,
              failureRate: metric.failure_rate_percent || 0,
            }
          })
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const metricsMap = new Map<string, { total: number; failed: number }>()

    // Prefer Phase 2/3 data: deployment failures and incidents
    const useAccurateData =
      (this.deployments.length > 0 && this.deploymentStatuses.length > 0) ||
      this.filteredIncidents.length > 0

    if (useAccurateData) {
      // Phase 2 & 3: Use actual deployment failures and incidents
      // Group deployments by month
      const deploymentsByMonth = new Map<string, DeploymentData[]>()
      for (const deployment of this.filteredDeployments) {
        const timestamp = deployment.created_at * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!deploymentsByMonth.has(period)) {
          deploymentsByMonth.set(period, [])
        }
        deploymentsByMonth.get(period)?.push(deployment)
      }

      // Count failures for each month
      for (const [period, deployments] of deploymentsByMonth.entries()) {
        let failedCount = 0

        for (const deployment of deployments) {
          const status = this.getDeploymentStatus(deployment.id)

          // Check if deployment failed OR caused an incident
          const deploymentFailed = status === "failure"
          const causedIncident = this.filteredIncidents.some((incident) => {
            if (!incident.incident_detected_at) return false

            // Check if incident was detected within 24 hours of deployment
            const incidentTime = incident.incident_detected_at
            const deploymentTime = deployment.created_at
            const timeDiff = incidentTime - deploymentTime

            return (
              timeDiff >= 0 &&
              timeDiff <= 24 * 60 * 60 && // Within 24 hours
              incident.repo_id === deployment.repo_id
            )
          })

          if (deploymentFailed || causedIncident) {
            failedCount++
          }
        }

        metricsMap.set(period, {
          total: deployments.length,
          failed: failedCount,
        })
      }
    } else {
      // Fallback: Use bugfix/revert commits after releases (estimation)
      const releasesByMonth = new Map<string, ReleaseData[]>()
      for (const release of this.filteredReleases) {
        const timestamp = (release.published_at || release.created_at) * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!releasesByMonth.has(period)) {
          releasesByMonth.set(period, [])
        }
        releasesByMonth.get(period)?.push(release)
      }

      for (const [period, releases] of releasesByMonth.entries()) {
        let failedCount = 0

        for (const release of releases) {
          const releaseTimestamp = release.published_at || release.created_at
          const windowEnd = releaseTimestamp + 7 * 24 * 60 * 60

          const hasFailure = this.commits.some(
            (commit) =>
              commit.repo_id === release.repo_id &&
              commit.timestamp > releaseTimestamp &&
              commit.timestamp <= windowEnd &&
              (commit.commit_type === "bugfix" || commit.commit_type === "revert"),
          )

          if (hasFailure) {
            failedCount++
          }
        }

        metricsMap.set(period, {
          total: releases.length,
          failed: failedCount,
        })
      }
    }

    const result: ChangeFailureMetric[] = []
    for (const [period, { total, failed }] of metricsMap.entries()) {
      result.push({
        period,
        totalDeployments: total,
        failedDeployments: failed,
        failureRate: total > 0 ? (failed / total) * 100 : 0,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average change failure rate
   * Phase 2 & 3: Uses deployment failures and incidents
   */
  get averageChangeFailureRate(): number {
    const useAccurateData =
      (this.deployments.length > 0 && this.deploymentStatuses.length > 0) ||
      this.filteredIncidents.length > 0

    if (useAccurateData) {
      // Phase 2 & 3: Use actual failures
      const totalDeployments = this.filteredDeployments.length
      if (totalDeployments === 0) return 0

      let failedCount = 0

      for (const deployment of this.filteredDeployments) {
        const status = this.getDeploymentStatus(deployment.id)
        const deploymentFailed = status === "failure"
        const causedIncident = this.filteredIncidents.some((incident) => {
          if (!incident.incident_detected_at) return false

          const incidentTime = incident.incident_detected_at
          const deploymentTime = deployment.created_at
          const timeDiff = incidentTime - deploymentTime

          return (
            timeDiff >= 0 && timeDiff <= 24 * 60 * 60 && incident.repo_id === deployment.repo_id
          )
        })

        if (deploymentFailed || causedIncident) {
          failedCount++
        }
      }

      return (failedCount / totalDeployments) * 100
    } else {
      // Fallback: Use bugfix/revert estimation
      const totalReleases = this.filteredReleases.length
      if (totalReleases === 0) return 0

      let failedCount = 0

      for (const release of this.filteredReleases) {
        const releaseTimestamp = release.published_at || release.created_at
        const windowEnd = releaseTimestamp + 7 * 24 * 60 * 60

        const hasFailure = this.commits.some(
          (commit) =>
            commit.repo_id === release.repo_id &&
            commit.timestamp > releaseTimestamp &&
            commit.timestamp <= windowEnd &&
            (commit.commit_type === "bugfix" || commit.commit_type === "revert"),
        )

        if (hasFailure) {
          failedCount++
        }
      }

      return (failedCount / totalReleases) * 100
    }
  }

  /**
   * Calculate time to restore service
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 3
   */
  get timeToRestore(): TimeToRestoreMetric[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      const monthlyMetrics = metrics.filter(
        (m) => m.period_type === "month" && m.incident_count !== undefined && m.incident_count > 0,
      )

      if (monthlyMetrics.length > 0) {
        return monthlyMetrics
          .map((metric) => {
            const date = new Date(metric.period_start * 1000)
            const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

            return {
              period,
              averageHours: metric.mttr_avg_hours || 0,
              medianHours: metric.mttr_median_hours || 0,
              incidents: metric.incident_count || 0,
            }
          })
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const metricsMap = new Map<string, number[]>()

    // Prefer Phase 3 incident data
    const useIncidents = this.filteredIncidents.length > 0

    if (useIncidents) {
      // Phase 3: Use actual incident detected → resolved time
      for (const incident of this.filteredIncidents) {
        if (!incident.incident_detected_at || !incident.incident_resolved_at) {
          continue // Skip unresolved or incomplete incidents
        }

        const restoreTimeHours =
          (incident.incident_resolved_at - incident.incident_detected_at) / 3600

        const date = new Date(incident.incident_detected_at * 1000)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!metricsMap.has(period)) {
          metricsMap.set(period, [])
        }
        // Only count positive restore times
        if (restoreTimeHours >= 0) {
          metricsMap.get(period)?.push(restoreTimeHours)
        }
      }
    } else {
      // Fallback: Estimate from bugfix/revert commits (rough approximation)
      const failureCommits = this.filteredCommits.filter(
        (c) => c.commit_type === "bugfix" || c.commit_type === "revert",
      )

      for (const failure of failureCommits) {
        const precedingRelease = this.releases
          .filter(
            (r) =>
              r.repo_id === failure.repo_id && (r.published_at || r.created_at) < failure.timestamp,
          )
          .sort((a, b) => {
            const aTime = a.published_at || a.created_at
            const bTime = b.published_at || b.created_at
            return bTime - aTime
          })[0]

        if (precedingRelease) {
          const releaseTime = precedingRelease.published_at || precedingRelease.created_at
          const restoreTimeHours = (failure.timestamp - releaseTime) / 3600

          const date = new Date(failure.timestamp * 1000)
          const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

          if (!metricsMap.has(period)) {
            metricsMap.set(period, [])
          }
          metricsMap.get(period)?.push(restoreTimeHours)
        }
      }
    }

    const result: TimeToRestoreMetric[] = []
    for (const [period, times] of metricsMap.entries()) {
      if (times.length === 0) continue

      times.sort((a, b) => a - b)
      const avg = times.reduce((sum, t) => sum + t, 0) / times.length
      const median = times[Math.floor(times.length / 2)]

      result.push({
        period,
        averageHours: avg,
        medianHours: median,
        incidents: times.length,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average time to restore
   * Phase 3: Uses actual incident data when available
   */
  get averageTimeToRestore(): {
    hours: number
    days: number
  } {
    const useIncidents = this.filteredIncidents.length > 0

    if (useIncidents) {
      // Phase 3: Use actual incident restore times
      const resolvedIncidents = this.filteredIncidents.filter(
        (i) => i.incident_detected_at && i.incident_resolved_at,
      )

      if (resolvedIncidents.length === 0) {
        return { hours: 0, days: 0 }
      }

      const totalRestoreTime = resolvedIncidents.reduce((sum, incident) => {
        if (!incident.incident_detected_at || !incident.incident_resolved_at) {
          return sum
        }
        return sum + (incident.incident_resolved_at - incident.incident_detected_at)
      }, 0)

      const avgHours = totalRestoreTime / resolvedIncidents.length / 3600

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    } else {
      // Fallback: Estimate from bugfix/revert commits
      const failureCommits = this.filteredCommits.filter(
        (c) => c.commit_type === "bugfix" || c.commit_type === "revert",
      )

      if (failureCommits.length === 0) {
        return { hours: 0, days: 0 }
      }

      let totalRestoreTime = 0
      let count = 0

      for (const failure of failureCommits) {
        const precedingRelease = this.releases
          .filter(
            (r) =>
              r.repo_id === failure.repo_id && (r.published_at || r.created_at) < failure.timestamp,
          )
          .sort((a, b) => {
            const aTime = a.published_at || a.created_at
            const bTime = b.published_at || b.created_at
            return bTime - aTime
          })[0]

        if (precedingRelease) {
          const releaseTime = precedingRelease.published_at || precedingRelease.created_at
          totalRestoreTime += failure.timestamp - releaseTime
          count++
        }
      }

      if (count === 0) {
        return { hours: 0, days: 0 }
      }

      const avgHours = totalRestoreTime / count / 3600

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    }
  }

  /**
   * Determine DORA performance level based on metrics
   * Based on 2024 DORA benchmarks
   */
  get performanceLevel(): DoraPerformanceLevel {
    const deployFreq = this.averageDeploymentFrequency.perDay
    const leadTime = this.averageLeadTime.hours
    const failureRate = this.averageChangeFailureRate
    const restoreTime = this.averageTimeToRestore.hours

    // Deployment Frequency
    let deploymentLevel: DoraPerformanceLevel["deploymentFrequency"]
    if (deployFreq >= 1) {
      deploymentLevel = "elite" // Multiple deploys per day
    } else if (deployFreq >= 1 / 7) {
      deploymentLevel = "high" // Once per week
    } else if (deployFreq >= 1 / 30) {
      deploymentLevel = "medium" // Once per month
    } else {
      deploymentLevel = "low" // Less than once per month
    }

    // Lead Time for Changes
    let leadTimeLevel: DoraPerformanceLevel["leadTime"]
    if (leadTime <= 24) {
      leadTimeLevel = "elite" // Less than one day
    } else if (leadTime <= 24 * 7) {
      leadTimeLevel = "high" // Less than one week
    } else if (leadTime <= 24 * 30) {
      leadTimeLevel = "medium" // Less than one month
    } else {
      leadTimeLevel = "low" // More than one month
    }

    // Change Failure Rate
    let failureRateLevel: DoraPerformanceLevel["changeFailureRate"]
    if (failureRate <= 5) {
      failureRateLevel = "elite" // 0-5%
    } else if (failureRate <= 10) {
      failureRateLevel = "high" // 5-10%
    } else if (failureRate <= 15) {
      failureRateLevel = "medium" // 10-15%
    } else {
      failureRateLevel = "low" // >15%
    }

    // Time to Restore Service
    let restoreTimeLevel: DoraPerformanceLevel["timeToRestore"]
    if (restoreTime <= 1) {
      restoreTimeLevel = "elite" // Less than one hour
    } else if (restoreTime <= 24) {
      restoreTimeLevel = "high" // Less than one day
    } else if (restoreTime <= 24 * 7) {
      restoreTimeLevel = "medium" // Less than one week
    } else {
      restoreTimeLevel = "low" // More than one week
    }

    // Overall level (most conservative)
    const levels = [deploymentLevel, leadTimeLevel, failureRateLevel, restoreTimeLevel]
    const levelScores = { elite: 4, high: 3, medium: 2, low: 1 }
    const avgScore = levels.reduce((sum, level) => sum + levelScores[level], 0) / levels.length

    let overall: DoraPerformanceLevel["overall"]
    if (avgScore >= 3.5) {
      overall = "elite"
    } else if (avgScore >= 2.5) {
      overall = "high"
    } else if (avgScore >= 1.5) {
      overall = "medium"
    } else {
      overall = "low"
    }

    return {
      deploymentFrequency: deploymentLevel,
      leadTime: leadTimeLevel,
      changeFailureRate: failureRateLevel,
      timeToRestore: restoreTimeLevel,
      overall,
    }
  }

  /**
   * Check if DORA metrics data is available
   */
  get hasData(): boolean {
    return this.releases.length > 0 || this.pullRequests.length > 0 || this.commits.length > 0
  }

  /**
   * Get data completeness information
   */
  get dataCompleteness(): {
    hasReleases: boolean
    hasPullRequests: boolean
    hasCommits: boolean
    hasDeployments: boolean
    hasWorkflowRuns: boolean
    hasIncidents: boolean
    missingDataPoints: DoraMissingDataKey[]
    dataQuality: "accurate" | "estimated" | "incomplete"
  } {
    const missingDataPoints: DoraMissingDataKey[] = []

    if (this.releases.length === 0) {
      missingDataPoints.push("doraMissingReleases")
    }
    if (this.pullRequests.length === 0) {
      missingDataPoints.push("doraMissingPullRequests")
    }
    if (this.commits.length === 0) {
      missingDataPoints.push("doraMissingCommits")
    }

    // Phase 2 completeness
    const hasDeployments = this.deployments.length > 0
    const hasWorkflowRuns = this.workflowRuns.length > 0
    if (!hasDeployments) {
      missingDataPoints.push("doraMissingDeployments")
    }
    if (!hasWorkflowRuns) {
      missingDataPoints.push("doraMissingWorkflowRuns")
    }

    // Phase 3 completeness
    const hasIncidents = this.filteredIncidents.length > 0
    if (!hasIncidents) {
      missingDataPoints.push("doraMissingIncidents")
    }

    // Determine data quality level
    let dataQuality: "accurate" | "estimated" | "incomplete"
    if (hasDeployments && hasIncidents) {
      dataQuality = "accurate" // Phase 2 & 3 implemented
    } else if (
      this.releases.length > 0 &&
      this.commits.length > 0 &&
      this.pullRequests.length > 0
    ) {
      dataQuality = "estimated" // Using proxies
    } else {
      dataQuality = "incomplete" // Missing core data
    }

    return {
      hasReleases: this.releases.length > 0,
      hasPullRequests: this.pullRequests.length > 0,
      hasCommits: this.commits.length > 0,
      hasDeployments,
      hasWorkflowRuns,
      hasIncidents,
      missingDataPoints,
      dataQuality,
    }
  }
}
